// The one source of a status pill's colour and wording, one map per family.
// Colour carries one meaning everywhere: success is done or good for the
// viewer, primary is in progress, warning needs action, secondary is closed or
// neutral, danger is a refusal the viewer must notice. A student and a
// reviewer reading the same candidacy stage get the same entry.

const GOOD = 'success';
const PROGRESS = 'primary';
const ACTION = 'warning';
const CLOSED = 'secondary';
const REFUSED = 'danger';

export const STATUS_FAMILIES = Object.freeze([
    'request',
    'programOutcome',
    'decision',
    'studentApplication',
    'taRequest',
    'candidacy',
    'offer',
]);

// A faculty request (Application.status)
export const REQUEST_PILL = Object.freeze({
    draft: { bg: CLOSED, label: 'Draft' },
    submitted: { bg: PROGRESS, label: 'Submitted' },
    under_review: { bg: PROGRESS, label: 'Under review' },
    revision_requested: { bg: ACTION, label: 'Revision requested' },
    decided: { bg: PROGRESS, label: 'Decided' },
    approved: { bg: GOOD, label: 'Approved' },
    rejected: { bg: REFUSED, label: 'Not selected' },
    waitlisted: { bg: CLOSED, label: 'Waitlisted' },
    withdrawn: { bg: CLOSED, label: 'Withdrawn' },
});

// A request its faculty member took back into revision themselves
export const BEING_REVISED_PILL = Object.freeze({
    bg: PROGRESS,
    label: 'Being revised',
});

// Where a request stands for one program (the server's programOutcome): the
// program's own decision, which is what its coordinator's list shows
export const PROGRAM_OUTCOME_PILL = Object.freeze({
    waiting: { bg: ACTION, label: 'Needs a decision' },
    revision: { bg: ACTION, label: 'Revision requested' },
    approved: { bg: GOOD, label: 'Approved' },
    provisional: { bg: PROGRESS, label: 'Contingent' },
    waitlisted: { bg: CLOSED, label: 'Waitlisted' },
    not_selected: { bg: REFUSED, label: 'Not selected' },
    elsewhere: { bg: CLOSED, label: 'Another program' },
    withdrawn: { bg: CLOSED, label: 'Withdrawn' },
});

// A program's decision on a request (Decision.type), refined by finalStatus
// for a contingent award
export const DECISION_PILL = Object.freeze({
    approved: { bg: GOOD, label: 'Approved' },
    partial: { bg: GOOD, label: 'Partially approved' },
    denied: { bg: REFUSED, label: 'Not selected' },
    waitlisted: { bg: CLOSED, label: 'Waitlisted' },
    contingent: { bg: PROGRESS, label: 'Contingent' },
    contingent_finalized: { bg: GOOD, label: 'Contingent award confirmed' },
    contingent_voided: { bg: CLOSED, label: 'Contingent award voided' },
});

// A faculty TA request (TARequest.status)
export const TA_REQUEST_PILL = Object.freeze({
    pending: { bg: ACTION, label: 'Pending review' },
    processing: { bg: PROGRESS, label: 'Processing' },
    approved: { bg: GOOD, label: 'Approved' },
    converted: { bg: GOOD, label: 'Converted' },
    denied: { bg: REFUSED, label: 'Not selected' },
    cancelled: { bg: CLOSED, label: 'Cancelled' },
});

// A student's employment application (StudentApplication.status)
export const STUDENT_APPLICATION_PILL = Object.freeze({
    draft: { bg: CLOSED, label: 'Draft' },
    submitted: { bg: PROGRESS, label: 'Submitted' },
    under_review: { bg: PROGRESS, label: 'Under review' },
    interview_scheduled: { bg: PROGRESS, label: 'Interview scheduled' },
    offered: { bg: ACTION, label: 'Offered' },
    accepted: { bg: GOOD, label: 'Accepted' },
    declined: { bg: CLOSED, label: 'Declined' },
    rejected: { bg: REFUSED, label: 'Not selected' },
    withdrawn: { bg: CLOSED, label: 'Withdrawn' },
});

// One stage of a candidacy, as the student and every reviewer read it.
// `unavailable` is what faculty receive for any closure that was not the
// student's own decline or withdrawal.
export const CANDIDACY_PILL = Object.freeze({
    applied: { bg: PROGRESS, label: 'Applied' },
    screening: { bg: PROGRESS, label: 'Being reviewed' },
    interview: { bg: PROGRESS, label: 'Interview' },
    finalist: { bg: PROGRESS, label: 'Finalist' },
    offered: { bg: ACTION, label: 'Offer made' },
    expired: { bg: ACTION, label: 'Offer expired' },
    accepted: { bg: GOOD, label: 'Accepted' },
    declined: { bg: CLOSED, label: 'Declined' },
    not_selected: { bg: CLOSED, label: 'Not selected' },
    withdrawn: { bg: CLOSED, label: 'Withdrawn' },
    closed: { bg: CLOSED, label: 'Closed' },
    unavailable: { bg: CLOSED, label: 'No longer available' },
    released: { bg: CLOSED, label: 'Position ended' },
});

// An offer, by where it stands. It reads like the candidacy stage it
// belongs to.
export const OFFER_PILL = Object.freeze({
    offered: CANDIDACY_PILL.offered,
    expired: CANDIDACY_PILL.expired,
    accepted: CANDIDACY_PILL.accepted,
    declined: CANDIDACY_PILL.declined,
    rescinded: { bg: CLOSED, label: 'Offer withdrawn' },
});

const MAPS = Object.freeze({
    request: REQUEST_PILL,
    programOutcome: PROGRAM_OUTCOME_PILL,
    decision: DECISION_PILL,
    studentApplication: STUDENT_APPLICATION_PILL,
    taRequest: TA_REQUEST_PILL,
    candidacy: CANDIDACY_PILL,
    offer: OFFER_PILL,
});

export function decisionKey(decision) {
    if (decision?.type !== 'contingent') return decision?.type;
    if (decision.finalStatus === 'finalized') return 'contingent_finalized';
    if (decision.finalStatus === 'voided') return 'contingent_voided';
    return 'contingent';
}

// A value a map does not know shows as itself, neutral, rather than
// breaking the screen.
export function pillFor(family, value, { application } = {}) {
    if (
        family === 'programOutcome' &&
        value === 'approved' &&
        application?.programOutcomePartial
    ) {
        return DECISION_PILL.partial;
    }
    if (
        ((family === 'request' && value === 'revision_requested') ||
            (family === 'programOutcome' && value === 'revision')) &&
        application?.revisionInitiatedBy === 'faculty'
    ) {
        return BEING_REVISED_PILL;
    }
    return MAPS[family]?.[value] || { bg: CLOSED, label: value };
}
